import {
  Button,
  ConfirmDialog,
  DataTable,
  ErrorState,
  Label,
  type LocalDataColumn,
  StatusBadge,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  TOUCH,
} from "@nymbl/nos2-rules";
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { STATUS_TONES } from "@/components/pto/pto-colors";
import { RejectPtoDialog } from "@/components/team/RejectPtoDialog";
import { OFF_KIND_TONES } from "@/components/team/team-colors";
import { useKeepPto, useRemovePto } from "@/hooks/use-pto";
import type { PtoRequestListState } from "@/hooks/use-pto-request-list";
import { PTO_REQUESTS_HELP } from "@/lib/column-help";
import { PTO_TAB_LABELS, PTO_TABS, type PtoEmailAction, type PtoTab } from "@/lib/enums";
import { withCells } from "@/lib/table-columns";
import { formatDays } from "@/lib/team/metrics";
import { isDecisionRunning, rowDecisionState } from "@/lib/team/pto-decisions";
import {
  canApprove,
  canRemove,
  emailDecision,
  formatDateRange,
  isCancelAsk,
  isUnbooked,
  type PtoRequestView,
  ptoTabLabel,
  requestsForTab,
  statusLabel,
} from "@/lib/team/pto-list";
import { OFF_KIND_LABELS, offKind } from "@/lib/team/time-off";
import type { IsoDate } from "@/lib/time/dates";
import { cn } from "@/lib/utils";
import type { PtoRequestsView } from "@/schemas/team";

const NO_ROWS: PtoRequestView[] = [];

const typeOf = (r: PtoRequestView) => {
  const kind = offKind(r.typeEnum);
  return kind === "other" ? r.typeName : OFF_KIND_LABELS[kind];
};
export const PTO_REQUEST_COLUMNS: LocalDataColumn<PtoRequestView>[] = [
  {
    key: "person",
    label: "Person",
    type: "text",
    accessor: (r) => r.personName,
    searchText: (r) => [r.description, r.deleteNote].filter(Boolean).join(" "),
    help: PTO_REQUESTS_HELP.person,
  },
  { key: "type", label: "Type", type: "enum", accessor: typeOf, help: PTO_REQUESTS_HELP.type },
  {
    key: "start",
    label: "Dates",
    type: "date",
    accessor: (r) => r.startDate,
    help: PTO_REQUESTS_HELP.start,
  },
  {
    key: "days",
    label: "Days",
    type: "number",
    accessor: (r) => r.days,
    help: PTO_REQUESTS_HELP.days,
  },
  {
    key: "status",
    label: "Status",
    type: "enum",
    accessor: statusLabel,
    help: PTO_REQUESTS_HELP.status,
  },
];

/**
 * The approve / book / reject writes, from the caller: My Team decides with scope "team", the
 * PTO page with scope "any". Each returns the mutation's promise so the list can hold the row.
 */
interface PtoDecisionCalls {
  approve: (id: string) => Promise<unknown>;
  book: (id: string) => Promise<unknown>;
  reject: (id: string, note: string) => Promise<unknown>;
}

const baseCells = {
  type: {
    render: (r: PtoRequestView) => {
      const kind = offKind(r.typeEnum);
      return <StatusBadge status={kind} tones={OFF_KIND_TONES} labels={{ [kind]: typeOf(r) }} />;
    },
  },
  start: { render: (r: PtoRequestView) => formatDateRange(r.startDate, r.endDate) },
  days: {
    render: (r: PtoRequestView) => <span className="font-semibold">{formatDays(r.days)}</span>,
  },
  status: {
    render: (r: PtoRequestView) => (
      <>
        <StatusBadge
          status={r.status}
          tones={STATUS_TONES}
          labels={{ [r.status]: statusLabel(r) }}
        />
        {r.status === "rejected" && r.rejectionNote && (
          <div className="mt-1 text-xs text-muted-foreground">{r.rejectionNote}</div>
        )}
        {isUnbooked(r) && <div className="mt-1 text-xs font-semibold text-danger">Not booked</div>}
      </>
    ),
  },
};

/** An approver's email button: the request and the step to open. */
export interface PtoEmailFocus {
  id: string;
  action: PtoEmailAction;
}

const EMAIL_DECISION_MESSAGES = {
  decided: "That request was already decided.",
  missing: "That request isn't waiting for you.",
} as const;

interface PtoRequestListProps {
  view: PtoRequestsView | undefined;
  /** When `view` was read; a row held after a 504 is released by a newer read. */
  viewUpdatedAt: number;
  isError: boolean;
  onRetry: () => void;
  tab: PtoTab;
  onTabChange: (tab: PtoTab) => void;
  calls: PtoDecisionCalls;
  /** Row locks and the holiday switch, from `usePtoRequestList` in the screen. */
  state: PtoRequestListState;
  /** Extra controls before "Show holidays" (the HR "Everyone" switch). */
  toolbar?: ReactNode;
  /** Open this request's approve / reject step once the list has loaded, then call `onFocusDone`. */
  focus?: PtoEmailFocus | null;
  onFocusDone?: () => void;
}

/**
 * To approve | Upcoming | Past with approve, reject, keep, remove and retry (spec 2.1), shared by
 * My Team's PTO panel and the PTO page. One write per row at a time; a row whose write succeeded
 * or may have saved (504) stays held until the list catches up, and other rows stay usable.
 */
export function PtoRequestList({
  view,
  viewUpdatedAt,
  isError,
  onRetry,
  tab,
  onTabChange,
  calls,
  state,
  toolbar,
  focus,
  onFocusDone,
}: PtoRequestListProps) {
  const switchId = useId();
  const listId = useId();
  const { decisions, decide, showHolidays, setShowHolidays } = state;
  const [rejecting, setRejecting] = useState<PtoRequestView | null>(null);
  const [removing, setRemoving] = useState<PtoRequestView | null>(null);
  const [approving, setApproving] = useState<PtoRequestView | null>(null);
  const keep = useKeepPto();
  const remove = useRemovePto();
  // Unsorted, rows keep the tab's own order (soonest first; Past most recent first).
  const rows = useMemo(
    () => (view ? requestsForTab(view.rows, tab, view.today, showHolidays) : NO_ROWS),
    [view, tab, showHolidays],
  );
  const columns = withCells(PTO_REQUEST_COLUMNS, {
    ...baseCells,
    person: {
      className: "min-w-40",
      render: (r) => {
        const { error } = rowDecisionState(decisions, r, viewUpdatedAt);
        return (
          <>
            <div className="font-semibold">{r.personName}</div>
            {r.description && <div className="text-sm text-muted-foreground">{r.description}</div>}
            {isCancelAsk(r) && r.deleteNote && <div className="text-sm">“{r.deleteNote}”</div>}
            {error && (
              <div role="alert" className="text-sm font-semibold text-danger">
                {error}
              </div>
            )}
          </>
        );
      },
    },
  });

  function rowButtons(r: PtoRequestView, today: IsoDate) {
    const { busy } = rowDecisionState(decisions, r, viewUpdatedAt);
    return (
      <div className="flex flex-wrap justify-end gap-2">
        {canApprove(r) && (
          <>
            <Button
              variant="outline"
              className={TOUCH}
              disabled={busy}
              aria-label={`Reject ${r.personName}, ${formatDateRange(r.startDate, r.endDate)}`}
              onClick={() => setRejecting(r)}
            >
              Reject
            </Button>
            <Button
              className={TOUCH}
              disabled={busy}
              aria-label={`Approve ${r.personName}, ${formatDateRange(r.startDate, r.endDate)}`}
              onClick={() => void decide(r, "approve", () => calls.approve(r.id))}
            >
              Approve
            </Button>
          </>
        )}
        {isCancelAsk(r) && (
          <Button
            variant="outline"
            className={TOUCH}
            disabled={busy}
            onClick={() => void decide(r, "keep", () => keep.mutateAsync({ id: r.id }))}
          >
            Keep
          </Button>
        )}
        {canRemove(r, today) && (
          <Button
            variant="outline"
            className={cn(TOUCH, "text-danger")}
            disabled={busy}
            onClick={() => setRemoving(r)}
          >
            Remove
          </Button>
        )}
        {isUnbooked(r) && (
          <Button
            size="sm"
            variant="outline"
            className={TOUCH}
            disabled={busy}
            onClick={() => void decide(r, "book", () => calls.book(r.id))}
          >
            Try again
          </Button>
        )}
      </div>
    );
  }

  // An email's Approve / Reject: once the list is in, open that step (nothing is decided until
  // the person confirms), or say why not; then the caller drops the link's params.
  const handled = useRef<string | null>(null);
  useEffect(() => {
    if (!focus || !view) return;
    const key = `${focus.id}:${focus.action}`;
    // The link's params drop on the next navigation; never open (or toast) it twice meanwhile.
    if (handled.current === key) return;
    handled.current = key;
    const found = emailDecision(view.rows, focus.id);
    if (found.kind === "decide") {
      if (focus.action === "approve") setApproving(found.row);
      else setRejecting(found.row);
    } else {
      toast.info(EMAIL_DECISION_MESSAGES[found.kind]);
    }
    onFocusDone?.();
  }, [focus, view, onFocusDone]);

  return (
    <>
      <Tabs
        value={tab}
        onValueChange={(v) => onTabChange(PTO_TABS.find((t) => t === v) ?? "pending")}
        className="min-w-0 gap-3"
      >
        <TabsList aria-label="Requests">
          {PTO_TABS.map((t) => (
            // Sized to its label: equal-width triggers clip "To approve (N)". No TabsContent:
            // one table holds each tab's rows, so the triggers point at it.
            <TabsTrigger
              key={t}
              value={t}
              className={cn(TOUCH, "flex-none")}
              aria-controls={listId}
            >
              {ptoTabLabel(t, view?.rows ?? NO_ROWS)}
            </TabsTrigger>
          ))}
        </TabsList>
        <div id={listId} className="min-w-0">
          <DataTable
            label={`${PTO_TAB_LABELS[tab]} requests`}
            search={{ placeholder: "Search requests…" }}
            exportName="pto-requests"
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            loading={!view && !isError}
            error={isError && !view ? new Error("Requests didn't load") : undefined}
            onRetry={onRetry}
            notice={
              isError && view ? (
                <ErrorState message="Requests didn't load" onRetry={onRetry} />
              ) : undefined
            }
            empty={{ title: "Nothing here" }}
            controls={
              <>
                {toolbar}
                <div className="flex min-h-11 items-center gap-2">
                  <Switch id={switchId} checked={showHolidays} onCheckedChange={setShowHolidays} />
                  <Label htmlFor={switchId} className="min-h-11 cursor-pointer whitespace-nowrap">
                    Show holidays
                  </Label>
                </div>
              </>
            }
            rowActions={(r) => (view ? rowButtons(r, view.today) : null)}
          />
        </div>
      </Tabs>
      <RejectPtoDialog
        key={rejecting?.id ?? "none"}
        request={rejecting}
        busy={rejecting ? isDecisionRunning(decisions, rejecting.id) : false}
        onReject={async (request, note) => {
          const result = await decide(request, "reject", () => calls.reject(request.id, note));
          // A 504 may have saved: close the modal; the row stays locked with its own line.
          return result.ok || result.maySaved;
        }}
        onClose={() => setRejecting(null)}
      />
      <ConfirmDialog
        open={approving !== null}
        onOpenChange={(open) => !open && setApproving(null)}
        title="Approve time off?"
        description={
          approving
            ? `${approving.personName} · ${approving.typeName} · ${formatDateRange(approving.startDate, approving.endDate)} · ${formatDays(approving.days)}`
            : ""
        }
        confirmLabel="Approve"
        onConfirm={async () => {
          const r = approving;
          setApproving(null);
          if (r) await decide(r, "approve", () => calls.approve(r.id));
        }}
      />
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title="Remove time off?"
        description={
          removing
            ? `${removing.personName} · ${formatDateRange(removing.startDate, removing.endDate)}`
            : ""
        }
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={async () => {
          const r = removing;
          setRemoving(null);
          // The row holds and carries any error line; the confirm never traps on a failure.
          if (r) await decide(r, "remove", () => remove.mutateAsync({ id: r.id }));
        }}
      />
    </>
  );
}
